// src/features/surveys/redux/surveySlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SurveyFormDto, SurveyQuestion, SurveyAnswer, SurveyParticipantListItem } from "../features/admin/surveys/types";
import { AnswerTypeEnum, QuestionOptionsOrientationEnum } from "../features/admin/surveys/types/enum";

export interface SurveyState {
    data: SurveyFormDto;
    errors: {
        [key: string]: string | undefined;
    };
    isSubmitting: boolean;
}

const initialState: SurveyState = {
    data: {
        id: 0,
        surveyName: "",
        campaignId: null,
        startDate: null,
        endDate: null,
        functionalAreaId: null,
        status: 0,
        questions: [],
        participants: {
            page: 1,
            size: 10,
            totalPages: 0,
            totalRecords: 0,
            items: []
    },
        sendBySMS: false,
        sendByEmail: false,
        campaignName: ""
    },
    errors: {},
    isSubmitting: false
};

const surveyFormSlice = createSlice({
    name: "surveyForm",
    initialState,
    reducers: {
        setError(state, action: PayloadAction<{ field: keyof SurveyFormDto | string; message: string | undefined }>) {
            const { field, message } = action.payload;
            state.errors[field] = message;
        },
        clearErrors(state) {
            state.errors = {};
        },
        setSurveyForm(state, action: PayloadAction<Partial<SurveyFormDto>>) {
            Object.assign(state.data, action.payload);  
        },
        addParticipants(state, action: PayloadAction<{ participants: SurveyParticipantListItem[]; }>) {
            const { participants } = action.payload;

            // Aseguramos la existencia de la estructura antes de asignar
            if (!state.data) state.data = {} as any;
            if (!state.data.participants) {
                state.data.participants = { items: [], totalRecords: 0, totalPages: 0, page: 1, size: 10 };
            }

            // Ahora la mutación es segura
            const currentItems = state.data.participants.items ?? [];
            state.data.participants.items = [...currentItems, ...participants];
            
            // Actualizamos contadores
            state.data.participants.totalRecords = state.data.participants.items.length;
            state.data.participants.totalPages = Math.ceil(
                state.data.participants.totalRecords / (state.data.participants.size || 1)
            );
        },
        resetSurvey() {
            return initialState;
        },
        addQuestion: (state) => {
            state.data.questions.push({
                id: 0,
                index: state.data.questions.length,
                text: {
                    catalan: '',
                    spanish: '',
                    english: '',
                    french: ''
                },
                code: '',
                description: '',
                order: state.data.questions.length + 1,
                helperText: {
                    catalan: '',
                    spanish: '',
                    english: '',
                    french: ''
                },
                minimumValue: 0,
                maximumValue: 0,
                numberOfDecimals: 0,
                numberDefaultValue: 0,
                isAnswerMandatory: true,
                answerType: AnswerTypeEnum['NPS' as keyof typeof AnswerTypeEnum],
                textDefaultValue: '',
                orientation: QuestionOptionsOrientationEnum.Vertical,
                answerOptions: []
            });
        },
        updateQuestion: (state, action: PayloadAction<{ index: number; field: keyof SurveyQuestion; value: any }>) => {
            const { index, field, value } = action.payload;

            const questions = state.data.questions;
            if (questions[index]) {
                if (field == 'order') {
                    const newOrder = value ? parseInt(value.toString()) : questions.length;
                    const oldOrder = questions[index].order;

                    if (!value) {
                        questions[index].order = questions.length;
                        questions.forEach((question, i) => {
                            if (i !== index && question.order > oldOrder) {
                                question.order -= 1;
                            }
                        });
                    }
                    else {
                        const targetOrder = Math.max(1, Math.min(newOrder, questions.length));
        
                        questions.forEach((question, i) => {
                            if (i === index)
                                return;
                            if (targetOrder <= question.order && question.order < oldOrder) {
                                question.order += 1;
                            }
                            else if (oldOrder < question.order && question.order <= targetOrder) {
                                question.order -= 1;
                            }
                        });
                        questions[index].order = targetOrder;
                    }
                } else {
                    // @ts-ignore
                    questions[index][field] = value;
                }
            }
        },
        removeQuestion: (state, action: PayloadAction<number>) => {
            const questions = state.data.questions;
            const questionOrderToDelete = questions[action.payload].order;

            questions.splice(action.payload, 1);
            questions.forEach((question, index) => {
                question.index = index;
                if (question.order > questionOrderToDelete)
                    question.order -= 1; 
            })
        },
        addAnswer: (state, action: PayloadAction<{ questionIndex: number }>) => {
            const { questionIndex } = action.payload;
            state.data.questions[questionIndex].answerOptions.push({
                id: 0,
                index: state.data.questions[questionIndex].answerOptions.length,
                text: {
                    catalan: '',
                    spanish: '',
                    english: '',
                    french: ''
                },
                value: 0,
                puntuation: 0,
                ordre: 0,
                selected: false
            });
        },
        updateAnswer: (state, action: PayloadAction<{ questionIndex: number; answerIndex: number; field: keyof SurveyAnswer; value: any }>) => {
            const { questionIndex, answerIndex, field, value } = action.payload;
            if (state.data.questions[questionIndex]) {
                // @ts-ignore
                state.data.questions[questionIndex].answerOptions[answerIndex][field] = value;
            }
        },
        removeAnswer: (state, action: PayloadAction<{ questionIndex: number; answerIndex: number }>) => {
            const { questionIndex, answerIndex } = action.payload;
            state.data.questions[questionIndex].answerOptions.splice(answerIndex, 1);
            state.data.questions[questionIndex].answerOptions.forEach((answer, index) => {
                answer.index = index;
            })
        },
        copyQuestion: (state, action: PayloadAction<number>) => {
            const source = state.data.questions[action.payload];
            if (!source) return;
            const newIndex = state.data.questions.length;
            state.data.questions.push({
                ...source,
                id: 0,
                index: newIndex,
                order: newIndex + 1,
                answerOptions: source.answerOptions.map((a, i) => ({ ...a, id: 0, index: i })),
            });
        },
    }
});

export const {
    setError,
    clearErrors,
    setSurveyForm,
    addParticipants,
    resetSurvey,
    addQuestion,
    updateQuestion,
    removeQuestion,
    copyQuestion,
    addAnswer,
    updateAnswer,
    removeAnswer
} = surveyFormSlice.actions;

export default surveyFormSlice.reducer;
